/**
 * The record of an import (P1-4): which table, when, by whom, what was taken, which rows were left
 * out and why, and the decisions for unknown colours and places. Stored as master data of type
 * `import`, so every device can show it later; the next upload takes the same decisions without
 * asking again (`rememberedChoices`).
 */
import type { Entity } from "./entities.ts";
import { plannedBottles } from "./catalog-import.ts";
import type { ImportChoices, ImportNote, ImportPlan } from "./catalog-import.ts";

export interface ImportDecision {
  column: "color" | "location";
  /** As written in the table. */
  value: string;
  /** The value normalised, as the import looks it up. */
  key: string;
  /** The colour, or the id of the location. */
  to: string;
  /** What to show: the colour, or „Weinkeller · Regal C“. */
  label: string;
  /** The location was created by this import. */
  created?: boolean;
  /** Rows without a colour (key „leer:…“): how many took it. Not remembered, the next table has other rows. */
  count?: number;
}

export interface ImportRecord {
  id: string;
  file: string;
  at: number;
  /** Account id of the person who took the table. */
  person?: string;
  counts: { created: number; updated: number; same: number; bottles: number; left: number };
  /** Rows left out, with the reason. */
  errors: ImportNote[];
  /** Rows taken, with something left out or worth knowing. */
  notes: ImportNote[];
  decisions: ImportDecision[];
  /** Errors and notes beyond those stored, for a table full of them. */
  more?: { errors: number; notes: number };
}

/** Errors and notes kept per import; a table with more is broken anyway. */
const LIMIT = 500;

/** The fields of the record for what `plan` takes. */
export function importRecord(plan: ImportPlan, meta: { file: string; at: number; person?: string }): Omit<ImportRecord, "id"> {
  const count = (action: string) => plan.rows.filter((r) => r.action === action).length;
  const decisions: ImportDecision[] = [
    ...plan.colors
      .filter((c) => c.color !== null)
      .map((c) => ({ column: "color" as const, value: c.value, key: c.key, to: c.color!, label: c.color!, ...(c.empty ? { count: c.lines.length } : {}) })),
    ...plan.places
      .filter((p) => p.target !== null)
      .map((p) => ({ column: "location" as const, value: p.value, key: p.key, to: p.target!.location, label: p.target!.label, ...(p.target!.created ? { created: true } : {}) })),
  ];
  const more = { errors: Math.max(0, plan.errors.length - LIMIT), notes: Math.max(0, plan.notes.length - LIMIT) };
  return {
    file: meta.file,
    at: meta.at,
    ...(meta.person ? { person: meta.person } : {}),
    counts: { created: count("create"), updated: count("update"), same: count("same"), bottles: plan.rows.reduce((s, r) => s + plannedBottles(r), 0), left: plan.errors.length },
    errors: plan.errors.slice(0, LIMIT),
    notes: plan.notes.slice(0, LIMIT),
    decisions,
    ...(more.errors || more.notes ? { more } : {}),
  };
}

const notes = (v: unknown): ImportNote[] =>
  Array.isArray(v) ? v.filter((n): n is ImportNote => typeof n?.line === "number" && typeof n?.message === "string") : [];

/** The record in a master data entity, or null if it is not one. */
export function importOf(e: Entity): ImportRecord | null {
  const f = e.fields;
  if (e.type !== "import" || typeof f.file !== "string" || typeof f.at !== "number") return null;
  const c = (f.counts ?? {}) as Record<string, unknown>;
  const n = (v: unknown) => (typeof v === "number" ? v : 0);
  const decisions = Array.isArray(f.decisions)
    ? (f.decisions as ImportDecision[]).filter((d) => (d?.column === "color" || d?.column === "location") && typeof d.key === "string" && typeof d.to === "string")
    : [];
  return {
    id: e.id,
    file: f.file,
    at: f.at,
    ...(typeof f.person === "string" ? { person: f.person } : {}),
    counts: { created: n(c.created), updated: n(c.updated), same: n(c.same), bottles: n(c.bottles), left: n(c.left) },
    errors: notes(f.errors),
    notes: notes(f.notes),
    decisions,
    ...(f.more && typeof f.more === "object" ? { more: f.more as ImportRecord["more"] } : {}),
  };
}

/** The decisions of earlier imports, the latest for each value: the next upload takes them without asking. */
export function rememberedChoices(records: Iterable<ImportRecord>): ImportChoices {
  const choices: ImportChoices = { colors: {}, places: {} };
  for (const r of [...records].sort((a, b) => a.at - b.at)) {
    for (const d of r.decisions) {
      if (d.column === "location") choices.places[d.key] = { kind: "place", id: d.to };
      else if (!d.key.startsWith("leer:")) choices.colors[d.key] = d.to;
    }
  }
  return choices;
}
